import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import SydInput from '../../../../../commonDesign/SydInput'

const INVALIDATION_TYPES = [
  { value: 'key', label: 'Query Key' },
  { value: 'query', label: 'Query Name' },
  { value: 'pipeline', label: 'Pipeline Code' }
]

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: 'var(--color-neutral-050)',
    borderBottom: '1px solid var(--color-neutral-200)',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: '12px',
    color: 'var(--text-color-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  count: {
    fontSize: '10px',
    color: 'var(--text-color-subtle)',
    backgroundColor: 'var(--color-neutral-200)',
    borderRadius: '8px',
    padding: '0 5px',
    lineHeight: '16px'
  },
  addBtn: {
    fontSize: '11px',
    color: 'var(--color-primary-500)',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    fontWeight: 600,
    '&:hover': {
      color: 'var(--color-primary-700)'
    }
  },
  body: {
    padding: '8px'
  },
  empty: {
    color: 'var(--text-color-subtle)',
    fontSize: '11px',
    textAlign: 'center',
    padding: '12px 8px'
  },
  row: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    '&:not(:last-child)': {
      marginBottom: '4px'
    }
  },
  typeSelect: {
    width: '120px',
    height: '40px',
    flexShrink: 0,
    padding: '2px 10px',
    boxSizing: 'border-box',
    borderRadius: 'var(--border-radius-sm)',
    border: 'var(--border-neutral-thin)',
    backgroundColor: 'var(--color-surface-050)',
    color: 'var(--text-color-main)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--root-font)'
  },
  valueInput: {
    flex: 1
  },
  removeBtn: {
    width: '22px',
    height: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: 'var(--color-neutral-400)',
    fontSize: '11px',
    borderRadius: '4px',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'var(--color-danger-050)',
      color: 'var(--color-danger-500)'
    }
  }
}))

function InvalidationsEditor ({ invalidations, onChange }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(true)
  const items = useMemo(() => invalidations || [], [invalidations])

  const handleAdd = useCallback((e) => {
    e.stopPropagation()
    onChange([...items, { key: [], _id: Date.now() }])
    setExpanded(true)
  }, [items, onChange])

  const handleRemove = useCallback((index) => {
    onChange(items.filter((_, i) => i !== index))
  }, [items, onChange])

  const handleUpdate = useCallback((index, updatedItem) => {
    const next = [...items]
    next[index] = updatedItem
    onChange(next)
  }, [items, onChange])

  const getType = (item) => {
    if ('pipeline' in item) return 'pipeline'
    if ('query' in item) return 'query'
    return 'key'
  }

  const getValue = (item) => {
    if ('pipeline' in item) return item.pipeline || ''
    if ('query' in item) return item.query || ''
    if (Array.isArray(item.key)) return item.key.join(', ')
    return ''
  }

  const handleTypeChange = useCallback((index, newType) => {
    const currentValue = getValue(items[index])
    const newItem = {}
    if (newType === 'pipeline') {
      newItem.pipeline = currentValue
    } else if (newType === 'query') {
      newItem.query = currentValue
    } else {
      newItem.key = currentValue ? currentValue.split(',').map(s => s.trim()) : []
    }
    handleUpdate(index, newItem)
  }, [items, handleUpdate])

  const handleValueChange = useCallback((index, value) => {
    const type = getType(items[index])
    const newItem = {}
    if (type === 'pipeline') {
      newItem.pipeline = value
    } else if (type === 'query') {
      newItem.query = value
    } else {
      newItem.key = value ? value.split(',').map(s => s.trim()) : []
    }
    handleUpdate(index, newItem)
  }, [items, handleUpdate])

  return (
    <div className={classes.root}>
      <div className={classes.header} onClick={() => setExpanded(!expanded)}>
        <span className={classes.headerTitle}>
          Invalidations
          <span className={classes.count}>{items.length}</span>
        </span>
        <button type='button' className={classes.addBtn} onClick={handleAdd}>+ Add</button>
      </div>
      {expanded && (
        <div className={classes.body}>
          {items.length === 0 ? (
            <div className={classes.empty}>Cache entries to clear after pipeline execution</div>
          ) : (
            items.map((item, index) => (
              <div key={item._id || index} className={classes.row}>
                <select
                  value={getType(item)}
                  onChange={(e) => handleTypeChange(index, e.target.value)}
                  className={classes.typeSelect}
                >
                  {INVALIDATION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <div className={classes.valueInput}>
                  <SydInput
                    value={getValue(item)}
                    onChange={(e) => handleValueChange(index, e.target.value)}
                    placeholder={getType(item) === 'key' ? 'key1, key2' : 'name'}
                    size='sm'
                  />
                </div>
                <button type='button' className={classes.removeBtn} onClick={() => handleRemove(index)}>&#10005;</button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

InvalidationsEditor.propTypes = {
  invalidations: PropTypes.array,
  onChange: PropTypes.func.isRequired
}

export default InvalidationsEditor
